import "amazon-connect-streams";
import { getLogger } from "@expert/logging";
import { CancelledError } from "@tanstack/react-query";

import type { AmazonConnectChatSDK } from "../amazonConnectChatSdk";

import { getParticipantIds } from "../utils/getParticipantIds";
import { getParticipantJoinTime, isParticipantEvent } from "../utils/transcriptEventsUtils";

const logger = getLogger({ module: "ConnectionEventSubscriptions" });

/**
 * Subscribes to all ChatJS events for a connection and forwards processed messages to the SDK.
 *
 * **Event Subscription Layer**  ChatJS events come through here, get processed, then forwarded.
 *
 * Handles messages from two sources:
 * 1. **Transcript sync** (onConnectionEstablished): Fires on initial connection and reconnection.
 *    Calls `syncChatTranscript()` to fetch and cache full transcript (cancels in-flight requests on reconnection).
 * 2. **Real-time messages** (onMessage): Processes incoming WebSocket events inline, then forwards to the SDK
 *
 * What this layer does:
 * - Subscribe to ChatJS events
 * - Filter
 * - Transform and/or get additional context
 * - Forward normalized messages/events to the SDK
 *
 * What this layer does NOT do:
 * - Chat Cache management
 * - UI callbacks
 * - Business logic
 *
 * @see https://github.com/amazon-connect/amazon-connect-chatjs?tab=readme-ov-file#chatsession-events
 *
 * @param connection The ChatJS `AgentChatSession` instance
 * @param chatSdk Our internal chat SDK to forward events to
 * @param contactId Contact ID for logging and context
 */
export function subscribeToConnectionEvents(
    connection: connect.AgentChatSession,
    chatSdk: AmazonConnectChatSDK,
    contact: connect.Contact,
): void {
    const { contactId } = contact;
    const contactLogger = logger.child({ contactId });
    const { agentParticipantId } = getParticipantIds(contact);

    connection.onConnectionEstablished(async () => {
        const actionLogger = contactLogger.child({ action: "onConnectionEstablished" });
        actionLogger.info("Chat connection established (initial or reconnection). Syncing transcript and attributes.");

        try {
            const messages = await chatSdk.syncChatTranscript(contact, { cancelInFlight: true });
            actionLogger.info({ messageCount: messages.length }, "Transcript synced and added to cache.");
        } catch (error) {
            if (error instanceof CancelledError) {
                actionLogger.debug("Transcript sync cancelled (superseded by newer request).");
            } else {
                actionLogger.warn({ error }, "Failed to sync transcript.");
            }
        }

        try {
            await chatSdk.syncContactAttributes(contact);
            actionLogger.info("Contact attributes synced and decrypted.");
        } catch (error) {
            actionLogger.warn({ error }, "Failed to sync contact attributes.");
        }
    });

    connection.onMessage((event) => {
        const actionLogger = contactLogger.child({ action: "onMessage" });
        if (!event?.data) {
            actionLogger.warn("Malformed message event: missing data");
            return;
        }

        const data = event.data;

        // Handle EVENT type (participant events, typing, receipts, etc.)
        if (data.Type === "EVENT") {
            if (isParticipantEvent(data.ContentType)) {
                const agentJoinTime = getParticipantJoinTime(data, agentParticipantId);
                if (agentJoinTime) {
                    chatSdk.handleAgentJoinTime(contactId, agentJoinTime);
                }
            }
            // Other event types (typing, receipts) are handled by dedicated handlers below
            return;
        }

        // Handle MESSAGE type (actual chat messages)
        if (data.Type === "MESSAGE") {
            if (data.ParticipantRole === "SYSTEM") {
                return;
            }

            // Skip partial Lex streaming message chunks — ChatJS will emit again when stitching is complete
            if (data.MessageMetadata?.MessageCompleted === false) {
                actionLogger.trace(
                    { messageId: data.Id, chunkNumber: data.MessageMetadata.ChunkNumber },
                    "Skipping partial Lex streaming message chunk",
                );
                return;
            }

            if (!data.Content || (typeof data.Content === "string" && data.Content.trim() === "")) {
                actionLogger.debug({ messageId: data.Id, contentType: data.ContentType }, "Skipping empty message");
                return;
            }

            actionLogger.trace(
                {
                    messageId: data.Id,
                    contentType: data.ContentType,
                },
                "Forwarding message to SDK for processing and decryption",
            );

            chatSdk
                .handleIncomingMessage(contactId, data)
                .catch((error) =>
                    actionLogger.error(
                        { error, messageId: data.Id, contentType: data.ContentType },
                        "Failed to process chat message",
                    ),
                );

            if (data.ParticipantRole === "CUSTOMER") {
                try {
                    chatSdk.setMessagesDelivered(contact, [data.Id]);
                } catch (error) {
                    actionLogger.warn({ error, messageId: data.Id }, "Failed to send delivered receipt");
                }
            }

            return;
        }

        // Log unhandled event types for debugging
        actionLogger.debug({ type: data.Type, contentType: data.ContentType }, "Received unhandled event type");
    });
    connection.onTyping((event) => {
        const actionLogger = contactLogger.child({ action: "onTyping" });
        if (!event?.data || event.data.ParticipantRole !== "CUSTOMER") {
            return;
        }
        actionLogger.trace({ participantRole: event.data.ParticipantRole }, "Customer typing");
        chatSdk.handleCustomerTyping(contactId);
    });

    connection.onReadReceipt((event) => {
        const actionLogger = contactLogger.child({ action: "onReadReceipt" });
        if (!event?.data || event.data.ParticipantRole !== "CUSTOMER") {
            return;
        }

        // data.Id is the metadata event's own ID the original message ID is in MessageMetadata.MessageId.
        const messageId = event.data.MessageMetadata?.MessageId;
        const eventId = event.data.Id;
        if (!messageId) {
            actionLogger.warn({ eventId }, "Customer read receipt missing MessageId — skipping");
            return;
        }

        try {
            chatSdk.handleCustomerReadReceipt(contactId, messageId);
            actionLogger.trace({ messageId, eventId }, "Customer read receipt processed");
        } catch (error) {
            actionLogger.warn({ error, messageId, eventId }, "Failed to handle customer read receipt");
        }
    });

    connection.onDeliveredReceipt((event) => {
        const actionLogger = contactLogger.child({ action: "onDeliveredReceipt" });
        // TODO: Update message status in cache when message is delivered to customer
        actionLogger.warn(
            {
                messageId: event.data.Id,
                participantRole: event.data.ParticipantRole,
            },
            "Delivered receipt - not implemented",
        );
    });

    // This event fires for both intentional (agent/customer disconnect) and unintentional (network issues) closures
    connection.onConnectionLost(() => {
        const actionLogger = contactLogger.child({ action: "onConnectionLost" });
        actionLogger.debug("WebSocket connection lost");
    });

    connection.onDeepHeartbeatFailure(() => {
        const actionLogger = contactLogger.child({ action: "onDeepHeartbeatFailure" });
        actionLogger.warn("WebSocket heartbeat failed — connection may be degraded");
    });

    connection.onDeepHeartbeatSuccess(() => {
        const actionLogger = contactLogger.child({ action: "onDeepHeartbeatSuccess" });
        actionLogger.trace("WebSocket heartbeat successful");
    });

    contactLogger.trace({ action: "subscribeToConnectionEvents" }, "Event subscriptions attached");
}
